import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings2, Play, Pause, ChevronLeft, ChevronRight,
  Home as HomeIcon, Check, Focus, Sparkles, GraduationCap, ChevronDown, ListChecks, X, Loader2,
  Image as ImageIcon, Music2, CloudRain, VolumeX, Landmark, Church, LayoutGrid,
} from "lucide-react";
import { getChapter } from "../api";
import { API, getRecap, chapterAudioUrl, AUDIO_TRANSLATIONS } from "../api";
import { getBookTheme, SECTION_LABEL, getSection } from "../lib/bookThemes";
import { useSettings } from "../context/SettingsContext";
import { useAtmosphere } from "../lib/useAtmosphere";
import {
  setLast, markChapterRead, isChapterRead,
} from "../lib/storage";
import { getPlan, flattenPlan } from "../data/readingPlans";
import { startPlan, markPlanDayDone } from "../lib/personal";
import { track } from "../lib/analytics";
import AmbientBackground from "../components/AmbientBackground";
import ChapterImage from "../components/ChapterImage";
import VerseBlock from "../components/VerseBlock";
import CharacterCardDialog from "../components/CharacterCardDialog";
import SettingsSheet from "../components/SettingsSheet";
import BookPicker from "../components/BookPicker";
import StudyPanel from "../components/StudyPanel";
import ExplainPanel from "../components/ExplainPanel";
import MemoryCheck from "../components/MemoryCheck";
import TranslationToggle from "../components/TranslationToggle";
import AppMenu from "../components/AppMenu";
import { usePremium } from "../context/PremiumContext";
import { tourActive } from "../lib/tour";
import { promptUpgrade, happyMoment } from "../lib/upgrade";
import { toast } from "sonner"; // eslint-disable-line no-unused-vars

export default function Reader() {
  const { book, chapter } = useParams();
  const navigate = useNavigate();
  const { isPremium, inApp } = usePremium();
  const featureLocked = () => inApp && !isPremium && !tourActive();
  const gotoPaywall = (f) => promptUpgrade(f);
  const openExplainPanel = (verse) => {
    if (featureLocked()) { gotoPaywall("Explain"); return; }
    setExplainVerse(verse);
    setExplainOpen(true);
  };
  const ch = parseInt(chapter, 10);
  const { focusMode, setFocusMode, translation, atmosphere, setAtmosphere } = useSettings();

  const [searchParams] = useSearchParams();
  const planId = searchParams.get("plan");
  const planObj = useMemo(() => (planId ? getPlan(planId) : null), [planId]);
  const planFlat = useMemo(() => flattenPlan(planObj), [planObj]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [studyOpen, setStudyOpen] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);
  const [explainVerse, setExplainVerse] = useState(null);
  const [explainMenuOpen, setExplainMenuOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeChar, setActiveChar] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [speakIdx, setSpeakIdx] = useState(-1);
  const [activeVerse, setActiveVerse] = useState(0);
  const [recap, setRecap] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [artworkOpen, setArtworkOpen] = useState(false);
  const [audioMenuOpen, setAudioMenuOpen] = useState(false);

  const speakRef = useRef({ cancelled: false });
  const completedRef = useRef(false);
  const verseEls = useRef([]);
  const tickRef = useRef(false);
  const audioRef = useRef(null);
  const wantPlay = useRef(false);
  const dataRef = useRef(null);
  const prevLoadRef = useRef({ book, ch, translation });
  const restoreVerseRef = useRef(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioTrans = AUDIO_TRANSLATIONS.includes((translation || "WEB").toUpperCase())
    ? (translation || "WEB").toUpperCase() : "WEB";
  const useOnyx = true; // Onyx always available — licensed texts use the WEB audio
  const audioIsWebFallback = audioTrans === "WEB" && (translation || "WEB").toUpperCase() !== "WEB";

  const imageSrc = data ? `${API}/bible/chapter-image/${data.book}/${data.chapter}?v=2` : null;

  useAtmosphere({ book: data?.book, chapter: data?.chapter, narrating: speaking, completed });

  // fetch chapter
  useEffect(() => {
    let active = true;
    // When only the translation changed (same chapter), remember the verse in
    // view so we can restore it after the new translation loads.
    const p = prevLoadRef.current;
    const transOnly = p.book === book && p.ch === ch && p.translation !== translation;
    if (transOnly && dataRef.current) {
      let topVerse = null;
      for (let i = 0; i < verseEls.current.length; i++) {
        const el = verseEls.current[i];
        if (!el) continue;
        if (el.getBoundingClientRect().bottom > 150) {
          topVerse = dataRef.current.verses[i]?.v ?? null;
          break;
        }
      }
      restoreVerseRef.current = topVerse;
    } else {
      restoreVerseRef.current = null;
    }
    prevLoadRef.current = { book, ch, translation };

    setLoading(true);
    setData(null);
    if (restoreVerseRef.current == null) window.scrollTo({ top: 0 });
    completedRef.current = false;
    verseEls.current = [];
    setActiveVerse(0);
    setRecap(null);
    setCompleted(false);
    getChapter(book, ch, translation)
      .then((d) => {
        if (!active) return;
        setData(d);
        dataRef.current = d;
        verseTimes.current = [];
        setLoading(false);
        setLast(d.book, d.chapter, d.book_name);
        if (d.fums_token && window.fums) {
          try { window.fums("trackView", d.fums_token); } catch (e) { /* noop */ }
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      stopSpeak();
    };
    // eslint-disable-next-line
  }, [book, ch, translation]);

  // After a translation switch, jump back to the verse that was in view.
  useEffect(() => {
    if (!data || restoreVerseRef.current == null) return;
    const target = restoreVerseRef.current;
    restoreVerseRef.current = null;
    const idx = data.verses.findIndex((v) => v.v === target);
    if (idx < 0) return;
    requestAnimationFrame(() => {
      const el = verseEls.current[idx];
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top: Math.max(0, y) });
      }
    });
  }, [data]);

  // scroll progress + active verse (focus) + auto-complete
  useEffect(() => {
    const compute = () => {
      tickRef.current = false;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const p = h > 0 ? window.scrollY / h : 1;
      const clamped = Math.min(1, Math.max(0, p));
      // quantize to 20 steps: the image's 700ms CSS transition smooths between
      // them. Per-frame updates restarted the transition ~60x/s (visible
      // flicker) and re-rendered the whole reader on every scroll frame.
      setProgress(Math.round(clamped * 20) / 20);

      // nearest verse to the reading focus line
      const focusLine = window.innerHeight * 0.42;
      let best = -1, bestDist = Infinity;
      for (let i = 0; i < verseEls.current.length; i++) {
        const el = verseEls.current[i];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const c = r.top + r.height / 2;
        const d = Math.abs(c - focusLine);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      if (best !== -1) setActiveVerse(best);

      if (p > 0.85 && data && !completedRef.current) {
        completedRef.current = true;
        setCompleted(true);
        track("chapter_complete", { book: data.book, chapter: data.chapter, translation });
        happyMoment();
        if (!isChapterRead(data.book, data.chapter)) {
          markChapterRead(data.book, data.chapter);
        }
        if (planObj) {
          const f = planFlat.find((x) => x.book === data.book && x.chapter === data.chapter);
          if (f) { startPlan(planId); markPlanDayDone(planId, f.dayIdx); }
        }
        getRecap(data.book, data.chapter).then(setRecap).catch(() => {});
      }
    };
    const onScroll = () => {
      if (tickRef.current) return;
      tickRef.current = true;
      requestAnimationFrame(compute);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", onScroll);
  }, [data, planObj, planFlat, planId]);

  // ----- read aloud -----
  const stopSpeak = useCallback(() => {
    speakRef.current.cancelled = true;
    window.speechSynthesis?.cancel();
    wantPlay.current = false;
    if (audioRef.current) audioRef.current.pause();
    setSpeaking(false);
    setSpeakIdx(-1);
  }, []);

  // Onyx narration (WEB/KJV) for the whole-chapter read-aloud
  const playOnyx = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    track("audio_play", { source: "reader", book: dataRef.current?.book, chapter: dataRef.current?.chapter });
    window.speechSynthesis?.cancel();
    wantPlay.current = true;
    setLoadingAudio(true);
    a.playbackRate = 0.95;
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  }, []);

  // Approximate per-verse timing so the text advances with the Onyx audio.
  // Duration is split across verses by character count (intro is spoken first).
  const verseTimes = useRef([]);
  const buildVerseTimes = useCallback(() => {
    const a = audioRef.current;
    const d = dataRef.current;
    if (!a || !d || !isFinite(a.duration) || a.duration <= 0) return;
    const introLen = (d.book_name?.length || 6) + 14;
    const lens = d.verses.map((v) => (v.t?.length || 1) + 1);
    const total = introLen + lens.reduce((s, n) => s + n, 0);
    let acc = introLen;
    const starts = lens.map((len) => {
      const start = (acc / total) * a.duration;
      acc += len;
      return start;
    });
    verseTimes.current = starts;
  }, []);

  const onAudioTime = useCallback(() => {
    const a = audioRef.current;
    const starts = verseTimes.current;
    if (!a || !starts.length) return;
    const t = a.currentTime;
    let idx = 0;
    for (let i = 0; i < starts.length; i++) {
      if (t >= starts[i]) idx = i; else break;
    }
    setSpeakIdx((prev) => {
      if (prev !== idx) {
        verseEls.current[idx]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return idx;
    });
  }, []);

  const speakChapter = useCallback(
    (startAt = 0) => {
      if (!data || !window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      speakRef.current.cancelled = false;
      setSpeaking(true);
      const verses = data.verses;
      const speakOne = (i) => {
        if (speakRef.current.cancelled || i >= verses.length) {
          setSpeaking(false);
          setSpeakIdx(-1);
          return;
        }
        setSpeakIdx(i);
        const el = document.querySelector(`[data-testid="verse-${verses[i].v}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        const u = new SpeechSynthesisUtterance(verses[i].t);
        u.rate = 0.95;
        u.onend = () => speakOne(i + 1);
        u.onerror = () => speakOne(i + 1);
        window.speechSynthesis.speak(u);
      };
      speakOne(startAt);
    },
    [data]
  );

  const speakFrom = (text) => {
    if (featureLocked()) { gotoPaywall("Audio narration"); return; }
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    window.speechSynthesis.speak(u);
  };

  const toggleSpeak = () => {
    if (featureLocked()) { gotoPaywall("Audio narration"); return; }
    if (useOnyx) {
      const a = audioRef.current;
      if (speaking && a) { wantPlay.current = false; a.pause(); setSpeaking(false); }
      else playOnyx();
    } else {
      if (speaking) stopSpeak();
      else speakChapter(0);
    }
  };

  const theme = getBookTheme(book);
  const section = getSection(book);

  // plan-aware navigation: when reading inside a plan, prev/next follow the plan sequence
  const planIdx = planObj && data
    ? planFlat.findIndex((f) => f.book === data.book && f.chapter === data.chapter)
    : -1;
  const planPrev = planIdx > 0 ? planFlat[planIdx - 1] : null;
  const planNext = planIdx >= 0 && planIdx < planFlat.length - 1 ? planFlat[planIdx + 1] : null;
  const prevRef = planObj ? planPrev : data?.prev;
  const nextRef = planObj ? planNext : data?.next;
  const planSuffix = planObj ? `?plan=${planId}` : "";

  const nextLabel = nextRef
    ? (nextRef.label || `${nextRef.book_name} ${nextRef.chapter}`)
    : null;

  const goPrev = () => prevRef && navigate(`/read/${prevRef.book}/${prevRef.chapter}${planSuffix}`);
  const goNext = () => nextRef && navigate(`/read/${nextRef.book}/${nextRef.chapter}${planSuffix}`);

  return (
    <div className="relative min-h-screen">
      <AmbientBackground theme={theme} />
      <ChapterImage src={imageSrc} progress={progress} />

      {/* top-left home */}
      <Link
        to="/"
        data-testid="home-link"
        className={`font-ui fixed left-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-primary/20 transition-transform hover:scale-105 active:scale-95 ${planObj ? "top-16" : "top-5"}`}
      >
        <HomeIcon className="h-6 w-6" />
      </Link>

      {/* atmosphere floater — a music note beside home that magnifies into the audio options */}
      <div className={`fixed left-[4.75rem] z-40 ${planObj ? "top-16" : "top-5"}`}>
        {audioMenuOpen && (
          <div
            className="fixed inset-0 z-0"
            data-testid="atmosphere-backdrop"
            onClick={() => setAudioMenuOpen(false)}
          />
        )}
        <button
          onClick={() => setAudioMenuOpen((o) => !o)}
          data-testid="atmosphere-toggle"
          className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border shadow-lg backdrop-blur-xl transition-all hover:scale-105 active:scale-95 ${
            atmosphere !== "off" ? "border-primary/40 bg-primary/15 text-primary ring-2 ring-primary/20" : "border-border bg-background/80 text-muted-foreground"
          }`}
          title="Reading atmosphere"
        >
          <Music2 className="h-5 w-5" />
          {atmosphere !== "off" && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background" />
          )}
        </button>

        {audioMenuOpen && (
          <div
            data-testid="atmosphere-menu"
            className="font-ui absolute left-0 top-full z-10 mt-2 w-52 origin-top-left animate-[atmoIn_140ms_ease-out] rounded-2xl border border-border bg-background/95 p-2 shadow-2xl backdrop-blur-xl"
          >
            <p className="px-2 pb-1 pt-1 text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">Reading atmosphere</p>
            {[
              { id: "off", label: "Off", sub: "Silent", icon: VolumeX },
              { id: "ambient", label: "Ambient", sub: "Soft music", icon: Music2 },
              { id: "hymns", label: "Hymns", sub: "Amazing Grace…", icon: Church },
              { id: "nature", label: "Nature", sub: "Rain, ocean…", icon: CloudRain },
              { id: "ancient", label: "Ancient", sub: "Old-world strings", icon: Landmark },
            ].map((o) => {
              const Ico = o.icon;
              const active = atmosphere === o.id;
              return (
                <button
                  key={o.id}
                  data-testid={`atmosphere-option-${o.id}`}
                  onClick={() => { setAtmosphere(o.id); setAudioMenuOpen(false); }}
                  className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors ${
                    active ? "bg-primary/15 text-primary" : "text-foreground hover:bg-muted"
                  }`}
                >
                  <Ico className={`h-4 w-4 flex-none ${active ? "text-primary" : "text-muted-foreground"}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{o.label}</span>
                    <span className="block truncate text-[0.7rem] text-muted-foreground">{o.sub}</span>
                  </span>
                  {active && <Check className="h-4 w-4 flex-none text-primary" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* translation toggle */}
      <div className={`fixed right-4 z-40 ${planObj ? "top-16" : "top-5"}`}>
        <TranslationToggle />
      </div>

      {/* plan mode ribbon — distinct emerald scheme so it's unmistakable & readable */}
      {planObj && (
        <div
          data-testid="plan-mode-banner"
          className="font-ui fixed inset-x-0 top-3 z-40 mx-auto flex w-fit max-w-[92vw] items-center gap-1.5 rounded-full border border-emerald-300/50 bg-emerald-600 py-1.5 pl-5 pr-2 text-sm font-bold text-white shadow-xl shadow-emerald-950/40"
        >
          <Link to={`/plans?plan=${planId}`} className="flex items-center gap-2.5 py-1 transition-transform hover:scale-[1.02]">
            <ListChecks className="h-4 w-4 shrink-0" />
            <span className="truncate">{planObj.title}</span>
            {planIdx >= 0 && (
              <span className="shrink-0 rounded-full bg-white/25 px-2.5 py-0.5 text-xs font-bold tracking-wide">
                Day {planFlat[planIdx].dayIdx + 1} / {planObj.days.length}
              </span>
            )}
          </Link>
          <button
            onClick={() => navigate(`/read/${data?.book || book}/${data?.chapter || ch}`, { replace: true })}
            data-testid="exit-plan-mode"
            title="Exit plan mode — back to regular reading"
            className="ml-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20 transition-colors hover:bg-white/40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* content */}
      <main className={`mx-auto max-w-3xl px-6 pb-40 ${planObj ? "pt-32 md:pt-40" : "pt-24 md:pt-32"}`}>
        {loading ? (
          <div className="space-y-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-8 w-full animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : data ? (
          <>
            {/* chapter header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mb-16 text-center"
            >
              <p className="font-ui mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-primary">
                {SECTION_LABEL[section]}
              </p>
              <button
                onClick={() => setPickerOpen(true)}
                data-testid="chapter-title-picker"
                className="group mx-auto block"
                title="Browse books & chapters"
              >
                <h1 className="font-serif-display inline-flex items-center gap-3 text-5xl tracking-tight transition-colors group-hover:text-primary md:text-6xl">
                  {data.book_name}
                  <ChevronDown className="h-7 w-7 text-muted-foreground transition-colors group-hover:text-primary" />
                </h1>
                <p className="font-ui mt-3 text-lg text-muted-foreground">
                  Chapter {data.chapter}
                  <span className="mx-2 text-border">·</span>
                  {data.verses.length} verses
                  {isChapterRead(data.book, data.chapter) && (
                    <span className="ml-2 inline-flex items-center gap-1 text-primary">
                      <Check className="h-4 w-4" /> read
                    </span>
                  )}
                </p>
              </button>
              <div className="mx-auto mt-8 h-px w-24 bg-gradient-to-r from-transparent via-primary to-transparent" />
              {data.copyright && (
                <p
                  data-testid="translation-copyright"
                  className="font-ui mx-auto mt-6 max-w-md text-[0.7rem] leading-relaxed text-muted-foreground/70"
                >
                  {data.copyright}
                </p>
              )}
            </motion.header>

            {/* verses */}
            <div className="space-y-10 md:space-y-12">
              {data.verses.map((v, i) => (
                <div
                  key={v.v}
                  ref={(el) => (verseEls.current[i] = el)}
                  className={speakIdx === i ? "rounded-xl ring-1 ring-primary/40" : ""}
                  style={speakIdx === i ? { background: "hsl(var(--muted) / 0.4)", padding: "0.75rem 1rem", margin: "-0.75rem -1rem" } : {}}
                >
                  {v.h && (
                    <p
                      data-testid={`verse-heading-${v.v}`}
                      className="font-ui mb-5 text-xs font-semibold uppercase tracking-[0.3em] text-primary/80"
                    >
                      {v.h}
                    </p>
                  )}
                  <VerseBlock
                    book={data.book}
                    bookName={data.book_name}
                    chapter={data.chapter}
                    verse={v}
                    onSpeak={speakFrom}
                    active={focusMode ? activeVerse === i : true}
                    onCharacter={(cid, name) => setActiveChar({ id: cid, name })}
                    onExplain={(vn) => {
                      track("explain_verse", { book: data.book, chapter: data.chapter, verse: vn });
                      openExplainPanel(vn);
                    }}
                  />
                </div>
              ))}
            </div>

            {/* end of chapter */}
            <div className="mt-24 text-center">
              <div className="mx-auto mb-8 h-px w-24 bg-gradient-to-r from-transparent via-border to-transparent" />
              <p className="font-ui mb-6 text-xs font-semibold uppercase tracking-[0.3em] text-primary">
                Chapter complete
              </p>

              {recap ? (
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mx-auto mb-10 max-w-xl rounded-3xl border border-border bg-background/50 p-8 text-left backdrop-blur-md"
                  data-testid="chapter-recap"
                >
                  <p className="font-ui mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    In one sentence
                  </p>
                  <p className="reading-text font-serif-display text-xl leading-relaxed">{recap.summary}</p>
                  <div className="mt-6 rounded-2xl bg-primary/10 p-5">
                    <p className="font-ui mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                      Reflect
                    </p>
                    <p className="reading-text italic leading-relaxed text-foreground/90">{recap.question}</p>
                  </div>
                </motion.div>
              ) : (
                <p className="font-serif-display mb-8 text-xl italic text-muted-foreground">
                  End of {data.book_name} {data.chapter}
                </p>
              )}

              {recap && <MemoryCheck book={data.book} chapter={data.chapter} bookName={data.book_name} />}

              {/* reward: reveal the full chapter artwork with no text over it */}
              <button
                onClick={() => setArtworkOpen(true)}
                data-testid="view-artwork-btn"
                className="font-ui group mx-auto mb-8 flex items-center gap-2.5 rounded-full border border-primary/40 bg-primary/10 px-6 py-3 text-sm font-semibold text-primary transition-all hover:scale-[1.03] hover:bg-primary/20"
              >
                <ImageIcon className="h-4 w-4" />
                Reveal the artwork
              </button>

              <div className="mb-2" />

              {nextRef ? (
                <button
                  onClick={goNext}
                  data-testid="next-chapter-cta"
                  className="font-ui group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
                >
                  {planObj ? "Next in plan: " : "Continue to "}{nextLabel}
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              ) : planObj ? (
                <Link
                  to={`/plans?plan=${planId}`}
                  data-testid="plan-complete-cta"
                  className="font-ui group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
                >
                  <Check className="h-4 w-4" /> Plan complete — view {planObj.title}
                </Link>
              ) : null}
            </div>
          </>
        ) : (
          <p className="text-center text-muted-foreground">Chapter not found.</p>
        )}
      </main>

      {/* floating control bar */}
      <div className="fixed bottom-20 left-1/2 z-40 -translate-x-1/2">
        <div className="font-ui flex items-center gap-0.5 rounded-full border border-border bg-background/80 p-1 shadow-2xl backdrop-blur-xl">
          <button
            onClick={goPrev}
            disabled={!prevRef}
            data-testid="prev-chapter"
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-muted disabled:opacity-30"
            title="Previous chapter"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            onClick={toggleSpeak}
            data-testid="read-aloud-toggle"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105"
            title={speaking ? "Pause narration" : useOnyx ? "Read aloud (Onyx voice)" : "Read aloud"}
          >
            {loadingAudio ? <Loader2 className="h-5 w-5 animate-spin" /> : speaking ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
          </button>

          <button
            onClick={goNext}
            disabled={!nextRef}
            data-testid="next-chapter"
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-muted disabled:opacity-30"
            title="Next chapter"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <button
            onClick={() => setFocusMode(!focusMode)}
            data-testid="quick-focus-toggle"
            className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-muted ${
              focusMode ? "text-primary" : "text-muted-foreground"
            }`}
            title="Toggle focus mode"
          >
            <Focus className="h-4 w-4" />
          </button>

          <div className="relative">
            <button
              onClick={() => {
                // In focus mode we know which verse the reader is on — let them
                // choose that verse or the whole chapter. Otherwise, chapter.
                if (featureLocked()) { gotoPaywall("Explain"); return; }
                if (focusMode) {
                  setExplainMenuOpen((o) => !o);
                } else {
                  track("explain_chapter", { book: data.book, chapter: data.chapter });
                  openExplainPanel(null);
                }
              }}
              data-testid="open-explain"
              className="flex h-8 items-center gap-1 rounded-full bg-primary/10 px-2 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/20"
              title="Explain in plain language"
            >
              <GraduationCap className="h-4 w-4 shrink-0" />
              <span>Explain</span>
            </button>

            {explainMenuOpen && focusMode && (
              <>
              <div className="fixed inset-0 z-40" onClick={() => setExplainMenuOpen(false)} data-testid="explain-choice-backdrop" />
              <div
                data-testid="explain-choice-menu"
                className="font-ui absolute bottom-full left-1/2 z-50 mb-2 w-52 -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-popover/95 shadow-xl backdrop-blur-md"
              >
                <button
                  onClick={() => {
                    const vn = data.verses[activeVerse]?.v;
                    setExplainMenuOpen(false);
                    track("explain_verse", { book: data.book, chapter: data.chapter, verse: vn, from: "focus" });
                    openExplainPanel(vn || null);
                  }}
                  data-testid="explain-choice-verse"
                  className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm transition-colors hover:bg-muted"
                >
                  <Focus className="h-4 w-4 shrink-0 text-primary" />
                  <span className="flex flex-col leading-tight">
                    <span className="font-semibold">This verse</span>
                    <span className="text-xs text-muted-foreground">{data.book_name} {data.chapter}:{data.verses[activeVerse]?.v}</span>
                  </span>
                </button>
                <div className="h-px bg-border" />
                <button
                  onClick={() => {
                    setExplainMenuOpen(false);
                    track("explain_chapter", { book: data.book, chapter: data.chapter, from: "focus" });
                    openExplainPanel(null);
                  }}
                  data-testid="explain-choice-chapter"
                  className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm transition-colors hover:bg-muted"
                >
                  <GraduationCap className="h-4 w-4 shrink-0 text-primary" />
                  <span>Whole chapter</span>
                </button>
              </div>
              </>
            )}
          </div>

          <button
            onClick={() => { track("study_open", { book: data.book, chapter: data.chapter }); setStudyOpen(true); }}
            data-testid="open-study"
            className="flex h-8 items-center gap-1 rounded-full bg-primary/10 px-2 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/20"
            title="AI study notes"
          >
            <Sparkles className="h-4 w-4 shrink-0" />
            <span>Study</span>
          </button>

          <button
            onClick={() => setSettingsOpen(true)}
            data-testid="open-settings"
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-muted"
            title="Settings"
          >
            <Settings2 className="h-4 w-4" />
          </button>

          <button
            onClick={() => { setMenuOpen(true); track("feature_open", { feature: "Tools", source: "reader_toolbar" }); }}
            data-testid="open-tools-menu"
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-muted"
            title="All tools & features"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* hidden Onyx audio element (WEB/KJV read-aloud) */}
      <audio
        ref={audioRef}
        preload="none"
        data-testid="reader-onyx-audio"
        src={useOnyx ? chapterAudioUrl(book, ch, audioTrans) : undefined}
        onWaiting={() => setLoadingAudio(true)}
        onLoadedMetadata={buildVerseTimes}
        onDurationChange={buildVerseTimes}
        onTimeUpdate={onAudioTime}
        onPlaying={() => { setLoadingAudio(false); setSpeaking(true); }}
        onCanPlay={() => {
          setLoadingAudio(false);
          buildVerseTimes();
          const a = audioRef.current;
          if (wantPlay.current && a && a.paused) a.play().then(() => setSpeaking(true)).catch(() => {});
        }}
        onPause={() => setSpeaking(false)}
        onEnded={() => { setSpeaking(false); setSpeakIdx(-1); wantPlay.current = false; }}
        onError={() => { setLoadingAudio(false); setSpeaking(false); }}
      />

      {/* speaking equalizer indicator */}
      {speaking && (
        <div className="fixed bottom-36 left-1/2 z-40 flex -translate-x-1/2 items-end gap-1 rounded-full border border-border bg-background/80 px-4 py-2 backdrop-blur-md">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="eq-bar w-1 rounded-full bg-primary"
              style={{ height: "60%", animationDelay: `${i * 0.12}s` }}
            />
          ))}
          <span className="font-ui ml-2 text-xs text-muted-foreground">{audioIsWebFallback ? "Onyx (WEB) narrating…" : "Onyx narrating…"}</span>
        </div>
      )}

      <SettingsSheet open={settingsOpen} onOpenChange={setSettingsOpen} />
      <BookPicker open={pickerOpen} onOpenChange={setPickerOpen} currentBook={book} />
      <AppMenu open={menuOpen} onOpenChange={setMenuOpen} />
      {data && (
        <StudyPanel open={studyOpen} onOpenChange={setStudyOpen} book={data.book} chapter={data.chapter} bookName={data.book_name} />
      )}
      {data && (
        <ExplainPanel
          open={explainOpen}
          onOpenChange={(o) => { setExplainOpen(o); if (!o) setExplainVerse(null); }}
          book={data.book}
          chapter={data.chapter}
          bookName={data.book_name}
          verse={explainVerse}
          verseText={explainVerse ? data.verses.find((x) => x.v === explainVerse)?.t : ""}
        />
      )}
      <CharacterCardDialog character={activeChar} onOpenChange={setActiveChar} showExploreLink />

      {/* full-screen artwork reward — the clean painting, no text overlaid */}
      <AnimatePresence>
        {artworkOpen && data && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black"
            data-testid="artwork-overlay"
            onClick={() => setArtworkOpen(false)}
          >
            <motion.img
              initial={{ scale: 1.04, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              src={imageSrc}
              alt={`${data.book_name} ${data.chapter} artwork`}
              className="max-h-full max-w-full object-contain"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent pb-8 pt-16 text-center">
              <p className="font-ui text-xs font-semibold uppercase tracking-[0.3em] text-primary">
                You reached the end
              </p>
              <p className="font-serif-display mt-1 text-2xl text-white">
                {data.book_name} {data.chapter}
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setArtworkOpen(false); }}
              data-testid="close-artwork-btn"
              className="font-ui absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-md transition-colors hover:bg-black/70"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
